import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { exerciseAPI } from '../services/api';
import { FaPlus, FaPlay, FaEdit, FaCopy, FaTrash, FaCheck, FaFileAlt } from 'react-icons/fa';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { format } from 'date-fns';

const Dashboard = () => {
  const { user } = useAuth();
  const [exercises, setExercises] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [exerciseToDelete, setExerciseToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editExercise, setEditExercise] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '', maxParticipants: 100 });
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [summaryExercise, setSummaryExercise] = useState(null);
  const [summaryPhases, setSummaryPhases] = useState([]);
  const [newExercise, setNewExercise] = useState({
    title: '',
    description: '',
    maxParticipants: 100
  });

  useEffect(() => {
    fetchExercises();
  }, []);

  const fetchExercises = async () => {
    try {
      const response = await exerciseAPI.getMyExercises();
      setExercises(response.data);
    } catch (error) {
      console.error('Failed to fetch exercises:', error);
    }
  };

  const handleCreateExercise = async () => {
    try {
      const response = await exerciseAPI.createExercise(newExercise);
      setExercises([response.data.exercise, ...exercises]);
      setShowCreateModal(false);
      setNewExercise({
        title: '',
        description: '',
        maxParticipants: 100
      });
    } catch (error) {
      console.error('Failed to create exercise:', error);
    }
  };

  const copyAccessCode = (code) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(code);
    } else {
      // Fallback for non-secure contexts (HTTP on LAN)
      const textarea = document.createElement('textarea');
      textarea.value = code;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
    showNotification('Access code copied to clipboard!', 'success');
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 3000);
  };

  const handleDeleteClick = (exercise) => {
    setExerciseToDelete(exercise);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!exerciseToDelete) return;

    try {
      await exerciseAPI.deleteExercise(exerciseToDelete._id);
      setExercises(exercises.filter(ex => ex._id !== exerciseToDelete._id));
      setShowDeleteModal(false);
      setExerciseToDelete(null);
      showNotification('Exercise deleted successfully!', 'success');
    } catch (error) {
      console.error('Failed to delete exercise:', error);
      showNotification('Failed to delete exercise. Please try again.', 'error');
      setShowDeleteModal(false);
      setExerciseToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setExerciseToDelete(null);
  };

  const handleEditClick = (exercise) => {
    setEditExercise(exercise);
    setEditForm({
      title: exercise.title,
      description: exercise.description,
      maxParticipants: exercise.maxParticipants || 50
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editExercise || !editForm.title.trim() || !editForm.description.trim()) return;

    try {
      await exerciseAPI.updateExercise(editExercise._id, editForm);
      setExercises(exercises.map(ex =>
        ex._id === editExercise._id ? { ...ex, ...editForm } : ex
      ));
      setShowEditModal(false);
      setEditExercise(null);
      showNotification('Exercise updated successfully!', 'success');
    } catch (error) {
      console.error('Failed to update exercise:', error);
      showNotification('Failed to update exercise. Please try again.', 'error');
    }
  };

  const handleActivateExercise = async (exerciseId) => {
    try {
      await exerciseAPI.updateExercise(exerciseId, { status: 'active' });
      // Refresh exercises list
      fetchExercises();
      showNotification('Exercise activated successfully!', 'success');
    } catch (error) {
      console.error('Failed to activate exercise:', error);
      showNotification('Failed to activate exercise. Please try again.', 'error');
    }
  };

  const handleOpenSummary = async (exercise) => {
    setSummaryExercise(exercise);
    try {
      const response = await exerciseAPI.getSummary(exercise._id);
      setSummaryPhases(response.data.summary || []);
    } catch (error) {
      console.error('Failed to load summary:', error);
      setSummaryPhases([]);
    }
    setShowSummaryModal(true);
  };

  const handleAddSummaryPhase = () => {
    const newPhase = {
      phaseNumber: summaryPhases.length + 1,
      title: '',
      description: ''
    };
    setSummaryPhases([...summaryPhases, newPhase]);
  };

  const handleUpdateSummaryPhase = (index, field, value) => {
    const updated = [...summaryPhases];
    updated[index][field] = value;
    setSummaryPhases(updated);
  };

  const handleDeleteSummaryPhase = (index) => {
    const updated = summaryPhases.filter((_, i) => i !== index);
    // Renumber phases
    updated.forEach((phase, i) => {
      phase.phaseNumber = i + 1;
    });
    setSummaryPhases(updated);
  };

  const handleSaveSummary = async () => {
    try {
      await exerciseAPI.updateSummary(summaryExercise._id, summaryPhases);
      showNotification('Summary saved successfully!', 'success');
      setShowSummaryModal(false);
      setSummaryExercise(null);
    } catch (error) {
      console.error('Failed to save summary:', error);
      showNotification('Failed to save summary. Please try again.', 'error');
    }
  };

  // Base64 upload adapter - converts images to inline base64
  function Base64UploadAdapterPlugin(editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
      return {
        upload: () => loader.file.then(file => new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve({ default: reader.result });
          reader.onerror = () => reject('Failed to read file');
          reader.readAsDataURL(file);
        })),
        abort: () => {}
      };
    };
  }

  // CKEditor configuration
  const editorConfig = {
    extraPlugins: [Base64UploadAdapterPlugin],
    toolbar: [
      'heading',
      '|',
      'bold',
      'italic',
      'link',
      '|',
      'bulletedList',
      'numberedList',
      '|',
      'outdent',
      'indent',
      '|',
      'imageUpload',
      'insertTable',
      '|',
      'blockQuote',
      '|',
      'undo',
      'redo'
    ],
    image: {
      toolbar: ['imageTextAlternative', 'imageStyle:inline', 'imageStyle:block', 'imageStyle:side']
    },
    table: {
      contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
    },
    link: {
      addTargetToExternalLinks: true
    },
    htmlSupport: {
      allow: [
        { name: /.*/, attributes: true, classes: true, styles: true }
      ]
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">Facilitator Dashboard</h1>
          <p className="text-gray-400">Welcome back, {user?.username}!</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600/80 hover:bg-blue-500/80 text-white px-4 py-2 rounded-lg flex items-center transition-all"
        >
          <FaPlus className="mr-2" />
          Create Exercise
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg shadow-xl p-6 border border-gray-700">
          <h3 className="text-lg font-bold mb-2 text-gray-300">Total Exercises</h3>
          <p className="text-3xl font-bold text-white">{exercises.length}</p>
        </div>
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg shadow-xl p-6 border border-gray-700">
          <h3 className="text-lg font-bold mb-2 text-gray-300">Active</h3>
          <p className="text-3xl font-bold text-white">
            {exercises.filter(e => e.status === 'active').length}
          </p>
        </div>
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg shadow-xl p-6 border border-gray-700">
          <h3 className="text-lg font-bold mb-2 text-gray-300">Draft</h3>
          <p className="text-3xl font-bold text-white">
            {exercises.filter(e => e.status === 'draft').length}
          </p>
        </div>
      </div>

      {/* Exercises List */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg shadow-2xl overflow-hidden border border-gray-700">
        <div className="px-6 py-4 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">Your Exercises</h2>
        </div>

        {exercises.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <FaPlus className="text-4xl mx-auto mb-3 text-gray-600" />
            <p>No exercises yet. Create your first exercise to get started!</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-700">
            {exercises.map((exercise) => (
              <div key={exercise._id} className="p-6 hover:bg-gray-700/30 transition-colors">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-bold text-lg mb-1 text-white">{exercise.title}</h3>
                    <p className="text-gray-400 mb-2">{exercise.description}</p>
                    <div className="flex items-center space-x-4 text-sm">
                      <span className={`px-2 py-1 rounded ${
                        exercise.status === 'active'
                          ? 'bg-emerald-500/10 text-emerald-400/80 border border-emerald-500/20'
                          : exercise.status === 'draft'
                          ? 'bg-amber-500/10 text-amber-400/80 border border-amber-500/20'
                          : 'bg-gray-700 text-gray-300'
                      }`}>
                        {exercise.status}
                      </span>
                      <span className="text-gray-400">Created: {format(new Date(exercise.createdAt), 'MMM d, yyyy')}</span>
                      <span className="flex items-center text-gray-400">
                        Access Code:
                        <code className="bg-gray-700 px-2 py-1 rounded ml-2 font-mono text-white border border-gray-600">
                          {exercise.accessCode}
                        </code>
                        <button
                          onClick={() => copyAccessCode(exercise.accessCode)}
                          className="ml-1 text-gray-400 hover:text-gray-300 transition-colors"
                        >
                          <FaCopy />
                        </button>
                      </span>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleOpenSummary(exercise)}
                      className="flex items-center bg-gray-700/50 text-gray-300 px-3 py-2 rounded hover:bg-gray-700 border border-gray-600 transition-colors"
                      title="Exercise Summary"
                    >
                      <FaFileAlt className="mr-1" />
                      Summary
                    </button>
                    {exercise.status === 'draft' && (
                      <button
                        onClick={() => handleActivateExercise(exercise._id)}
                        className="flex items-center bg-gray-700/50 text-gray-300 px-3 py-2 rounded hover:bg-gray-700 border border-gray-600 transition-colors"
                        title="Activate Exercise"
                      >
                        <FaCheck className="mr-1" />
                        Activate
                      </button>
                    )}
                    <button
                      onClick={() => handleEditClick(exercise)}
                      className="flex items-center bg-gray-700/50 text-gray-300 px-3 py-2 rounded hover:bg-gray-700 border border-gray-600 transition-colors"
                      title="Edit Exercise Details"
                    >
                      <FaEdit className="mr-1" />
                      Edit
                    </button>
                    <Link
                      to={`/exercise/${exercise._id}/build`}
                      className="flex items-center bg-gray-700/50 text-gray-300 px-3 py-2 rounded hover:bg-gray-700 border border-gray-600 transition-colors"
                      title="Build Injects"
                    >
                      <FaPlus className="mr-1" />
                      Build
                    </Link>
                    <Link
                      to={`/exercise/${exercise._id}/control`}
                      className="flex items-center bg-gray-700/50 text-gray-300 px-3 py-2 rounded hover:bg-gray-700 border border-gray-600 transition-colors"
                      title="Control Exercise"
                    >
                      <FaPlay className="mr-1" />
                      Control
                    </Link>
                    <button
                      onClick={() => handleDeleteClick(exercise)}
                      className="flex items-center bg-red-500/10 text-red-400/80 px-3 py-2 rounded hover:bg-red-500/20 border border-red-500/20 transition-colors"
                      title="Delete Exercise"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Notification */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-50 animate-fade-in-down">
          <div className={`rounded-lg shadow-2xl p-4 border ${
            notification.type === 'success'
              ? 'bg-gray-800 text-gray-200 border-gray-600'
              : 'bg-gray-800 text-gray-200 border-gray-600'
          }`}>
            <p className="font-medium">{notification.message}</p>
          </div>
        </div>
      )}

      {/* Create Exercise Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg shadow-2xl w-full max-w-md border border-gray-700">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4 text-white">Create New Exercise</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Exercise Title *
                  </label>
                  <input
                    type="text"
                    value={newExercise.title}
                    onChange={(e) => setNewExercise({ ...newExercise, title: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/30"
                    placeholder="e.g., Cyber Attack Simulation"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Description *
                  </label>
                  <textarea
                    value={newExercise.description}
                    onChange={(e) => setNewExercise({ ...newExercise, description: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/30"
                    rows="3"
                    placeholder="Brief description of the exercise..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Max Participants
                  </label>
                  <input
                    type="number"
                    value={newExercise.maxParticipants}
                    onChange={(e) => setNewExercise({ ...newExercise, maxParticipants: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/30"
                    min="1"
                    max="100"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateExercise}
                  className="bg-blue-600/80 hover:bg-blue-500/80 text-white px-4 py-2 rounded transition-all font-medium"
                >
                  Create Exercise
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg shadow-2xl w-full max-w-md border border-gray-700">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="bg-red-500/10 p-3 rounded-full mr-4">
                  <FaTrash className="text-2xl text-red-400/80" />
                </div>
                <h3 className="text-xl font-bold text-white">Delete Exercise</h3>
              </div>

              <p className="text-gray-300 mb-2">
                Are you sure you want to delete this exercise?
              </p>
              {exerciseToDelete && (
                <div className="bg-gray-700/50 rounded-lg p-3 mb-4 border border-gray-600">
                  <p className="font-bold text-white">{exerciseToDelete.title}</p>
                  <p className="text-sm text-gray-400">{exerciseToDelete.description}</p>
                </div>
              )}
              <p className="text-sm text-gray-400 mb-4">
                This action cannot be undone. All exercise data, injects, and participant responses will be permanently deleted.
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={handleDeleteCancel}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="bg-red-600/80 hover:bg-red-500/80 text-white px-4 py-2 rounded transition-all font-medium"
                >
                  Delete Exercise
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Exercise Modal */}
      {showEditModal && editExercise && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg shadow-2xl w-full max-w-md border border-gray-700">
            <div className="p-6">
              <h3 className="text-xl font-bold mb-4 text-white">Edit Exercise</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Exercise Title *
                  </label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/30"
                    placeholder="e.g., Cyber Attack Simulation"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Description *
                  </label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/30"
                    rows="3"
                    placeholder="Brief description of the exercise..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Max Participants
                  </label>
                  <input
                    type="number"
                    value={editForm.maxParticipants}
                    onChange={(e) => setEditForm({ ...editForm, maxParticipants: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/30"
                    min="1"
                    max="100"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => { setShowEditModal(false); setEditExercise(null); }}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEditSave}
                  className="bg-blue-600/80 hover:bg-blue-500/80 text-white px-4 py-2 rounded transition-all font-medium"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary Modal */}
      {showSummaryModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg shadow-2xl w-full max-w-4xl border border-gray-700 my-8">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h3 className="text-xl font-bold text-white">Exercise Summary / Drill Presentation</h3>
                  <p className="text-gray-400 text-sm">{summaryExercise?.title}</p>
                </div>
                <button
                  onClick={handleAddSummaryPhase}
                  className="bg-blue-600/80 hover:bg-blue-500/80 text-white px-4 py-2 rounded-lg flex items-center transition-all"
                >
                  <FaPlus className="mr-2" />
                  Add Phase
                </button>
              </div>

              <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
                {summaryPhases.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <FaFileAlt className="text-4xl mx-auto mb-3 text-gray-600" />
                    <p>No summary phases added yet.</p>
                    <p className="text-sm mt-1">Click "Add Phase" to create your first summary phase.</p>
                  </div>
                ) : (
                  summaryPhases.map((phase, index) => (
                    <div key={index} className="bg-gray-700/50 rounded-lg p-4 border border-gray-600">
                      <div className="flex justify-between items-start mb-4">
                        <span className="bg-gray-600 text-gray-300 px-3 py-1 rounded text-sm font-semibold border border-gray-500">
                          Phase {phase.phaseNumber}
                        </span>
                        <button
                          onClick={() => handleDeleteSummaryPhase(index)}
                          className="text-gray-400 hover:text-gray-300 transition-colors"
                        >
                          <FaTrash />
                        </button>
                      </div>

                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Phase Title *
                        </label>
                        <input
                          type="text"
                          value={phase.title}
                          onChange={(e) => handleUpdateSummaryPhase(index, 'title', e.target.value)}
                          className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50 focus:border-blue-500/30"
                          placeholder="e.g., LONG-TERM RECONNAISSANCE (Pre-attack months)"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-1">
                          Description *
                        </label>
                        <div className="bg-white rounded-md ckeditor-container">
                          <CKEditor
                            editor={ClassicEditor}
                            config={editorConfig}
                            data={phase.description || ''}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              handleUpdateSummaryPhase(index, 'description', data);
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-700">
                <button
                  onClick={() => { setShowSummaryModal(false); setSummaryExercise(null); }}
                  className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveSummary}
                  className="bg-blue-600/80 hover:bg-blue-500/80 text-white px-6 py-2 rounded transition-all font-medium"
                >
                  Save Summary
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
